import { Camera } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';

interface ScanAnotherProps {
  /** Files chosen via the camera input. */
  onFiles: (files: File[]) => void;
  lang: UiLangCode;
}

/**
 * Mobile-only quick-action: a "Scan another" button that opens the rear
 * camera directly via `capture="environment"`. Mirrors the takePhoto
 * label-and-input pattern from the Dropzone but lives below the
 * sessions list so a user mid-stack doesn't have to scroll back up to
 * the dropzone after each scan.
 *
 * Hidden on `sm:` and up — desktop has the always-visible dropzone, no
 * need for a duplicate button.
 */
export function ScanAnother({ onFiles, lang }: ScanAnotherProps) {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files === null || files.length === 0) return;
    onFiles(Array.from(files));
    // Reset so picking the same file again still fires change.
    e.target.value = '';
  };

  return (
    <div className="flex justify-center sm:hidden">
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm shadow-indigo-500/20 transition-all hover:bg-indigo-700 hover:shadow-md focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-within:ring-offset-slate-950">
        <Camera className="h-4 w-4" aria-hidden="true" />
        {t(lang, 'scan.another')}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onChange}
          aria-label={t(lang, 'scan.another')}
          className="sr-only"
        />
      </label>
    </div>
  );
}
